import {
  isOfficeViewerSupported,
  useIsOfficeViewerEnabled,
  OfficeDocViewer,
} from '@/components/common/OfficeDocViewer';
import type { ZoomState } from '@/components/common/useZoom';
import { XlsxViewer } from './XlsxViewer';

interface ExcelDocViewerProps {
  /** S3 URL for the local XLSX parser (the fallback viewer) */
  url: string;
  fileName: string;
  mimeType?: string;
  previewPlainTextAsTable?: boolean;
  /** Document ID for the Office Online viewer (omit to force the fallback) */
  documentId?: string;
  height?: string | number;
  /**
   * Zoom owned by the host page (`useZoom` + `ZoomControls` in its own
   * toolbar). Applies to both viewers: the grid rescales its cells, the
   * Office iframe is CSS-scaled.
   */
  zoom?: ZoomState;
}

/**
 * Excel document viewer: renders the Microsoft Office Online viewer whenever
 * it can (cleaner than the local parser), silently falling back to the
 * built-in XLSX parser when Office Online isn't available:
 * - the `msft-office-viewer-enabled` feature flag is off,
 * - no `documentId` is provided, or
 * - the file extension isn't supported by Office Online.
 */
export function ExcelDocViewer({
  url,
  fileName,
  mimeType,
  previewPlainTextAsTable,
  documentId,
  height = 450,
  zoom,
}: ExcelDocViewerProps) {
  const officeViewerEnabled = useIsOfficeViewerEnabled();
  const canUseOfficeViewer =
    officeViewerEnabled && !!documentId && isOfficeViewerSupported(fileName);

  if (!canUseOfficeViewer) {
    return (
      <XlsxViewer
        url={url}
        fileName={fileName}
        mimeType={mimeType}
        previewPlainTextAsTable={previewPlainTextAsTable}
        height={height}
        zoom={zoom}
      />
    );
  }

  return (
    <OfficeDocViewer
      documentId={documentId}
      height={height}
      zoom={zoom?.zoom}
      className="rounded-md border"
    />
  );
}
